import React, { FC, useMemo } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import Button from '../Button';
import { IconCalendar } from '../icons';
import { CalendarDay } from './CalendarDay';
import { useNavigate } from 'react-router-dom';
import { ResourceIcon } from './ResourceIcon';
import { BookingSlot, BookingResource, UserBooking } from '../../types/booking';
import { useResourceSchema } from '../../hooks/useResourceSchema';
import { useResourceBookings } from '../../hooks/useBookings';
import {
  mapAndFillApiSlots,
  groupApiSlotsByDay,
} from '../../utils/slotGeneration';

const DAYS_SHOWN = 7;

interface Props {
  resource: BookingResource;
  userBookings: UserBooking[];
  onSlotSelect?: (slot: BookingSlot) => void;
}
export const QuickBookingRow: FC<Props> = ({
  resource,
  userBookings,
  onSlotSelect,
}) => {
  const navigate = useNavigate();
  const today = DateTime.now().startOf('day');

  const {
    data: iloqSchema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useResourceSchema({
    resourceId: resource.id,
  });

  const { data: existingBookings = [] } = useResourceBookings(resource.id);

  const resourceUserBookings = useMemo(
    () =>
      userBookings?.filter((booking) => booking.resourceId === resource.id) ||
      [],
    [userBookings, resource.id],
  );

  const slotsByDay = useMemo(() => {
    const mappedSlots = mapAndFillApiSlots(
      iloqSchema?.days,
      existingBookings,
      resourceUserBookings,
    );
    return groupApiSlotsByDay(mappedSlots);
  }, [iloqSchema, existingBookings, resourceUserBookings]);

  return (
    <div className="flex flex-col w-full gap-2">
      <div className="flex items-center gap-3">
        <ResourceIcon type={resource.type} className="size-10" />
        <h3 className="text-lg text-gray-800">{resource.name}</h3>
        <Button
          className="ml-auto"
          startIcon={<IconCalendar />}
          onClick={() => navigate(`/booking/${resource.id}`)}
        >
          <FormattedMessage defaultMessage="Visa kalender" />
        </Button>
      </div>

      {!schemaError ? (
        <div
          className="grid gap-1 w-full"
          style={{ gridTemplateColumns: `repeat(${DAYS_SHOWN}, 1fr)` }}
        >
          {Array.from({ length: DAYS_SHOWN }).map((_, i) => {
            const date = today.plus({ days: i });
            const daySlots = slotsByDay.get(date.toISODate() || '') || [];

            return (
              <CalendarDay
                key={date.toISODate()}
                date={date}
                slots={daySlots}
                isLoading={isLoadingSchema}
                onSlotSelect={onSlotSelect}
                viewContext="quickBooking"
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
